/**
 * The theme token contract.
 *
 * A theme is data: a flat map of CSS custom-property values. Applying one
 * writes those values as inline styles on <html>, which override the defaults
 * baked into :root in index.css. Nothing else in the app knows a theme exists
 * — components only ever reference semantic utilities (bg-card, text-danger-ink),
 * so every surface recolors at once and none of them can drift.
 *
 * All values are oklch(). That matters: oklch lightness is perceptually
 * uniform, so holding L constant across hues keeps a chip row visually even,
 * and contrast can be reasoned about from the L value alone rather than
 * measured per swatch.
 */

/** The eight semantic tone families every status color resolves to. */
export const TONE_NAMES = [
  'danger',
  'caution',
  'warning',
  'success',
  'fresh',
  'info',
  'accent1',
  'accent2',
  'accent3',
  'neutral',
] as const;

export type ToneName = (typeof TONE_NAMES)[number];

/** Per-tone slots. See index.css for what each is for. */
export interface ToneSlots {
  /** Solid mid-hue: solid fills, and /10 /15 /30 tint washes. */
  base: string;
  /** Tone-colored text on the app background. Target >= 4.5:1. */
  ink: string;
  /** Opaque pale wash for chip backgrounds. */
  surface: string;
  /** Hairline border pairing with `surface`. */
  line: string;
  /** Text sitting on top of `base`. */
  on: string;
}

/** The core shadcn surface//text tokens, plus the nav rail set. */
export interface CoreTokens {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  sidebar: string;
  sidebarForeground: string;
  sidebarPrimary: string;
  sidebarPrimaryForeground: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  sidebarBorder: string;
  sidebarRing: string;
  /** The logo's cherry, on light surfaces. */
  brandCherry: string;
  /** The logo's cherry, lifted for the dark rail. */
  brandCherryOnRail: string;
  /** The logo's stem. */
  brandStem: string;
  /** Chart series 1-5. */
  chart: [string, string, string, string, string];
}

/**
 * Tone overrides applied inside `.surface-dark` (the nav rail and mobile dock).
 * Only the three slots that break on a dark ground need restating; `base` and
 * `on` carry over unchanged.
 */
export type ToneRailOverrides = Pick<ToneSlots, 'ink' | 'surface' | 'line'>;

export interface Theme {
  id: string;
  /** Display name in the picker. */
  name: string;
  /** One line on what the scheme is going for. */
  description: string;
  /**
   * Whether content surfaces are light or dark. Drives the `theme-color` meta
   * tag choice and lets the picker group schemes.
   */
  mode: 'light' | 'dark';
  core: CoreTokens;
  tones: Record<ToneName, ToneSlots>;
  railTones: Record<ToneName, ToneRailOverrides>;
  /** Border radius scale root. Defaults to 0.75rem when omitted. */
  radius?: string;
  /**
   * Color the OS paints browser/PWA chrome with. iOS 26 samples the page
   * rather than reading the meta tag, but Android and desktop still use it.
   */
  chromeColor: string;
}

/** camelCase token key -> the CSS custom property it maps to. */
const CORE_VAR: Record<Exclude<keyof CoreTokens, 'chart' | 'brandCherryOnRail'>, string> = {
  background: '--background',
  foreground: '--foreground',
  card: '--card',
  cardForeground: '--card-foreground',
  popover: '--popover',
  popoverForeground: '--popover-foreground',
  primary: '--primary',
  primaryForeground: '--primary-foreground',
  secondary: '--secondary',
  secondaryForeground: '--secondary-foreground',
  muted: '--muted',
  mutedForeground: '--muted-foreground',
  accent: '--accent',
  accentForeground: '--accent-foreground',
  destructive: '--destructive',
  destructiveForeground: '--destructive-foreground',
  border: '--border',
  input: '--input',
  ring: '--ring',
  sidebar: '--sidebar',
  sidebarForeground: '--sidebar-foreground',
  sidebarPrimary: '--sidebar-primary',
  sidebarPrimaryForeground: '--sidebar-primary-foreground',
  sidebarAccent: '--sidebar-accent',
  sidebarAccentForeground: '--sidebar-accent-foreground',
  sidebarBorder: '--sidebar-border',
  sidebarRing: '--sidebar-ring',
  brandCherry: '--brand-cherry',
  brandStem: '--brand-stem',
};

/**
 * Flatten a theme into the `--var: value` pairs that go on <html>.
 *
 * The rail overrides are deliberately NOT included: they belong to the
 * `.surface-dark` scope, not the root, and are emitted separately by
 * `railToneCss` into a stylesheet.
 */
export function themeToVars(theme: Theme): Record<string, string> {
  const vars: Record<string, string> = {};

  for (const [key, cssVar] of Object.entries(CORE_VAR)) {
    vars[cssVar] = theme.core[key as keyof typeof CORE_VAR];
  }

  theme.core.chart.forEach((value, i) => {
    vars[`--chart-${i + 1}`] = value;
  });

  for (const tone of TONE_NAMES) {
    const slots = theme.tones[tone];
    vars[`--tone-${tone}`] = slots.base;
    vars[`--tone-${tone}-ink`] = slots.ink;
    vars[`--tone-${tone}-surface`] = slots.surface;
    vars[`--tone-${tone}-line`] = slots.line;
    vars[`--tone-${tone}-on`] = slots.on;
  }

  vars['--radius'] = theme.radius ?? '0.75rem';

  return vars;
}

/**
 * The `.surface-dark` half of a theme, as a CSS rule body.
 *
 * `.surface-dark` is an @utility, so it can't be extended from JS by setting
 * inline vars — it has to be a real rule. We emit one <style> element and
 * rewrite its contents on every theme change.
 */
export function railToneCss(theme: Theme): string {
  const decls: string[] = [`--brand-cherry: ${theme.core.brandCherryOnRail};`];

  for (const tone of TONE_NAMES) {
    const rail = theme.railTones[tone];
    decls.push(`--tone-${tone}-ink: ${rail.ink};`);
    decls.push(`--tone-${tone}-surface: ${rail.surface};`);
    decls.push(`--tone-${tone}-line: ${rail.line};`);
  }

  return `.surface-dark{${decls.join('')}}`;
}
